import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Card, Divider, IconButton, Slider, Stack, TextField, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ReactPlayer from 'react-player';
import Loading from '../../../components/loading/Loading';

// m:ss, or h:mm:ss once there is an hour to show — the same shape the event
// timeline above the player uses, so the two readouts agree.
export function formatClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// Deliberately no scrub bar here: the event timeline below the player is the
// recording's scrubber, and it moves the sensor data with the video. A second
// one that only moved the video would let the two disagree about "now".

SessionPageVideoPlayer.propTypes = {
  session: PropTypes.object,
  isPlaying: PropTypes.bool,
  videoFileLocation: PropTypes.string,
  videoRef: PropTypes.object,
  onVideoReady: PropTypes.func,
  playClicked: PropTypes.func,
  pauseClicked: PropTypes.func,
  resetClicked: PropTypes.func,
  lagTime: PropTypes.number,
  lagTimeChanged: PropTypes.func,
  videoRotation: PropTypes.number,
  onRotate: PropTypes.func,
};

function SessionPageVideoPlayer({
  session,
  isPlaying = false,
  videoFileLocation,
  videoRef,
  onVideoReady,
  playClicked,
  pauseClicked,
  resetClicked,
  lagTime,
  lagTimeChanged,
  videoRotation = 0,
  onRotate = () => {},
}) {
  const [videoUrl, setVideoUrl] = useState('');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    setVideoUrl(videoFileLocation);
  }, [session, videoFileLocation]);

  const handleOnReady = () => {
    setIsVideoReady(true);
    onVideoReady();
  };

  const updateLagTime = (event) => {
    lagTimeChanged(parseInt(event.target.value, 10) * 1000);
  };

  const enterFullscreen = () => {
    const el = frameRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen();
  };

  const VolumeIcon = muted || volume === 0 ? VolumeOffIcon : (volume < 0.5 && VolumeDownIcon) || VolumeUpIcon;

  const player = (
    <ReactPlayer
      ref={videoRef}
      url={videoUrl}
      playing={isPlaying}
      onReady={handleOnReady}
      volume={volume}
      muted={muted}
      onDuration={setDuration}
      onProgress={({ playedSeconds }) => setPlayed(playedSeconds)}
      progressInterval={500}
      width="100%"
      // No height override: react-player's own default is what sized the
      // picture before, and `height: 100%` inside an auto-height flex parent
      // resolves to nothing.
    />
  );

  return (
    <Card>
      {/* A dark stage the video sits on. Recordings are often portrait, and
          against the card's white they read as a stray black rectangle rather
          than as the player's picture. */}
      <Box
        ref={frameRef}
        sx={{
          position: 'relative',
          bgcolor: 'common.black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxHeight: 420,
          overflow: 'hidden',
        }}
      >
        {!isVideoReady && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <Loading />
          </Box>
        )}
        {videoRotation ? (
          <div
            style={{
              transform: `rotate(${videoRotation}deg) translate3d(0, 0, 0)`,
              transformOrigin: 'center center',
              willChange: 'transform',
            }}
          >
            {player}
          </div>
        ) : (
          player
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 2, py: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton
            color="primary"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={isPlaying ? pauseClicked : playClicked}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Restart">
          <IconButton color="primary" aria-label="Restart" onClick={resetClicked}>
            <ReplayIcon />
          </IconButton>
        </Tooltip>

        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', ml: 0.5 }}>
          {`${formatClock(played)} / ${formatClock(duration)}`}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 140 }}>
          <Tooltip title={muted ? 'Unmute' : 'Mute'}>
            <IconButton
              color="primary"
              aria-label={muted ? 'Unmute' : 'Mute'}
              onClick={() => setMuted((prev) => !prev)}
            >
              <VolumeIcon />
            </IconButton>
          </Tooltip>
          <Slider
            aria-label="Volume"
            size="small"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(_, v) => {
              setVolume(v);
              // Moving the slider is a request to hear something.
              if (v > 0) setMuted(false);
            }}
          />
        </Stack>

        <Tooltip title="Rotate video">
          <IconButton color="primary" aria-label="Rotate video" onClick={onRotate}>
            <Rotate90DegreesCwIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fullscreen">
          <IconButton color="primary" aria-label="Fullscreen" onClick={enterFullscreen}>
            <FullscreenIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />

        {/* Not a video control, but it has always lived here: how far before an
            event the playhead lands. */}
        <Tooltip title="How many seconds before an event to start playing">
          <TextField
            id="lagTimeNumber"
            label="Lag Time"
            type="number"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={Number.isNaN(lagTime) ? '' : lagTime / 1000}
            onChange={updateLagTime}
            sx={{ width: 104 }}
          />
        </Tooltip>
      </Stack>
    </Card>
  );
}

export default SessionPageVideoPlayer;
